import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { ApiResponse } from '../axios';
import { ENDPOINTS } from '../endpoints';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../errors';

export interface MessageSendResult {
  success: boolean;
  messageId?: string;
  channel: string;
  recipient: string;
  externalMessageId?: string;
  errorMessage?: string;
  sentAt: string;
}

export interface MessageHistoryItem {
  id: string;
  channel: string;
  messageType: string;
  status: string;
  statusCode: string;
  recipient: string;
  subject?: string;
  content: string;
  sentAt?: string;
  deliveredAt?: string;
  failureReason?: string;
  createdAt: string;
}

export interface FailedNotificationItem {
  ticketId: string;
  ticketNumber: string;
  customerName: string;
  ticketStatus: string;
  failureReason?: string;
  failedAt: string;
}

export const notificationKeys = {
  ticket: (ticketId: string) => ['notifications', 'ticket', ticketId] as const,
  failed: ['notifications', 'failed'] as const,
};

// Message history for a ticket (what was sent to the customer + delivery status)
export function useTicketMessages(ticketId: string) {
  return useQuery({
    queryKey: notificationKeys.ticket(ticketId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<MessageHistoryItem[]>>(
        ENDPOINTS.NOTIFICATIONS.TICKET_HISTORY(ticketId)
      );
      return response.data.data ?? [];
    },
    enabled: !!ticketId,
    // Overrides the app-wide 5-minute default (main.tsx). Every status-change mutation now
    // invalidates this on success, but the actual send is a background Hangfire job (see
    // RepairTicketService.QueueCustomerNotification) - the invalidate can still win a race against
    // the job writing its Messages row. staleTime: 0 means the next thing that asks (a manual
    // reopen of this panel, a focus event) always gets the real current state instead of trusting a
    // refetch that landed a beat too early.
    staleTime: 0,
  });
}

// Open tickets whose customer message failed on every channel and wasn't sent since
export function useFailedNotifications() {
  return useQuery({
    queryKey: notificationKeys.failed,
    queryFn: async () => {
      const response = await api.get<ApiResponse<FailedNotificationItem[]>>(ENDPOINTS.NOTIFICATIONS.FAILED);
      return response.data.data ?? [];
    },
    refetchInterval: 60_000,
    staleTime: 0,
  });
}

// Manually send a status update to the customer.
// Without customMessage the backend renders the template for the ticket's current status.
export function useSendCustomerUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customMessage }: { ticketId: string; customMessage?: string }) => {
      const response = await api.post<ApiResponse<MessageSendResult>>(ENDPOINTS.NOTIFICATIONS.STATUS_UPDATE, {
        ticketId,
        useTemplate: !customMessage,
        customMessage: customMessage || undefined,
      });
      return response.data.data;
    },
    onSuccess: (result, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(ticketId) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.failed });
      // The API call itself succeeding (HTTP 200) only means the request was processed - the
      // actual WhatsApp/SMS/Email send has its own success flag nested in the response, since a
      // provider rejection (e.g. missing credentials) is a normal outcome, not an HTTP error.
      // Showing "sent" regardless of this flag was actively misleading.
      if (result?.channel === 'Skipped') {
        toast.error('העדכון לא נשלח - שליחת הודעות בסטטוס הזה כבויה בהגדרות ההתראות');
      } else if (result?.success) {
        toast.success('העדכון נשלח ללקוח');
      } else {
        toast.error(result?.errorMessage || 'שליחת העדכון נכשלה - בדקו את חיבור הוואטסאפ/SMS בהגדרות');
      }
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת העדכון ללקוח'));
    },
  });
}
